import React, { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Coins, ShoppingBag, Download, Image as ImageIcon, ArrowRight, Camera, Phone, Mail, MapPin, User, History } from "lucide-react";
import { customerApi, fmtErr } from "@/customer/api";
import { useCustomerAuth } from "@/customer/AuthContext";
import { useCart } from "@/customer/CartContext";

const Card = ({ label, value, icon: Icon, accent = "magenta", testId }) => (
  <div data-testid={testId} className="relative glass chrome-border rounded-md p-5 lift overflow-hidden">
    <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full ${accent === "cyan" ? "bg-[#18d3ff]/15" : "bg-[#ff2e93]/15"} blur-2xl pointer-events-none`} />
    <div className="relative flex items-start justify-between gap-3">
      <div>
        <p className="text-[10px] tracking-luxe uppercase text-white/45 mb-2">{label}</p>
        <p className="font-display text-3xl text-chrome leading-none">{value}</p>
      </div>
      {Icon && <div className="w-10 h-10 rounded-full chrome-border bg-black/40 flex items-center justify-center shrink-0">
        <Icon className={`w-4 h-4 ${accent === "cyan" ? "text-[#18d3ff]" : "text-[#ff2e93]"}`} />
      </div>}
    </div>
  </div>
);

const CreditHistory = () => {
  const [tx, setTx] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    customerApi.get("/customer/credits")
      .then((r) => { if (active) setTx(r.data); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);
  if (loading) return <p className="text-sm text-white/40">Loading…</p>;
  if (tx.length === 0) return <div className="glass chrome-border rounded-md p-6 text-center text-white/55 text-sm">No credit transactions yet. Pay outside the website and credits will appear here once added by admin.</div>;
  return (
    <div className="glass chrome-border rounded-md divide-y divide-white/5">
      {tx.slice(0,6).map((t) => (
        <div key={t.id} data-testid={`customer-tx-${t.id}`} className="p-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm text-white">{t.type === "add" ? "Credits Added" : t.type === "remove" ? "Credits Removed" : "Adjustment"}</p>
            <p className="text-[10px] tracking-luxe uppercase text-white/45 mt-0.5">{t.created_at ? new Date(t.created_at).toLocaleString("en-ZA",{dateStyle:"medium",timeStyle:"short"}) : "—"}</p>
            {(t.customer_note || t.note) && <p className="text-xs text-white/55 mt-1 truncate">{t.customer_note || t.note}</p>}
          </div>
          <div className="text-right shrink-0">
            <p className={`font-display text-xl ${t.type === "add" ? "text-[#7fe6ff]" : "text-[#ff9fcf]"}`}>{t.type === "add" ? "+" : "−"}{t.amount}</p>
            <p className="text-[10px] tracking-luxe uppercase text-white/40">Balance: {t.balance_after ?? "—"}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function CustomerDashboard() {
  const { user } = useCustomerAuth();
  const { count: cartCount } = useCart();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasedCount, setPurchasedCount] = useState(null);
  const [downloadCount, setDownloadCount] = useState(null);

  useEffect(() => {
    let active = true;
    customerApi.get("/public/events")
      .then((r) => { if (active) setEvents(r.data); })
      .catch((e) => toast.error(fmtErr(e)))
      .finally(() => { if (active) setLoading(false); });
    customerApi.get("/customer/purchased-photos")
      .then((r) => { if (active) setPurchasedCount(r.data.length); })
      .catch(() => {});
    customerApi.get("/customer/downloads")
      .then((r) => { if (active) setDownloadCount(r.data.length); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  return (
    <div data-testid="page-customer-dashboard" className="max-w-[1280px] mx-auto px-5 sm:px-8 pt-32 pb-24 space-y-10">
      <SEO title="CustomerDashboard · WelkomBiz Photography" noindex />
      {/* Welcome */}
      <header className="space-y-3">
        <p className="text-[11px] tracking-luxe uppercase text-white/45">— Customer Dashboard</p>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[1.05]">
          <span className="text-chrome">Welcome back,</span>{" "}
          <span className="italic text-magcyan">{user?.name || "friend"}</span>
          <span className="text-chrome">.</span>
        </h1>
        <p className="text-white/65 font-body max-w-2xl">
          Browse your event galleries, view watermarked previews and prepare to download your favourite
          moments once credits are available.
        </p>
      </header>

      {/* Stat cards */}
      <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        <Card testId="customer-stat-credits"   label="Available Credits"  value={user?.credit_balance ?? 0} icon={Coins} accent="magenta" />
        <Card testId="customer-stat-cart"      label="In Cart"            value={cartCount} icon={ShoppingBag} accent="cyan" />
        <Card testId="customer-stat-purchased" label="Purchased Photos"   value={purchasedCount ?? user?.purchases ?? 0} icon={ImageIcon} accent="magenta" />
        <Card testId="customer-stat-downloads" label="Downloads"          value={downloadCount ?? 0} icon={Download} accent="cyan" />
        <Card testId="customer-stat-events"    label="Events Available"   value={events.length} icon={Camera} accent="magenta" />
      </section>

      {/* Credits info */}
      <div className="glass chrome-border rounded-md p-5 sm:p-6 flex items-start gap-4">
        <Coins className="w-4 h-4 text-[#ff2e93] mt-1 shrink-0" />
        <p className="text-sm text-white/65 font-body">
          Credits unlock full-quality event photos. Select your favourites from any gallery, add them to your cart, and confirm the purchase using credits.
          Full-quality downloads activate in the next phase — until then, purchased previews stay safe in your library.
        </p>
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap gap-3">
        <Link to="/events" data-testid="customer-qa-browse"  className="btn-primary !py-2.5 !px-5 !text-[11px]"><Camera className="w-3.5 h-3.5" /> Browse Events</Link>
        <Link to="/customer/cart" data-testid="customer-qa-cart" className="btn-ghost !py-2.5 !px-5 !text-[11px]"><ShoppingBag className="w-3.5 h-3.5" /> Cart{cartCount > 0 ? ` · ${cartCount}` : ""}</Link>
        <Link to="/customer/purchased" data-testid="customer-qa-purchased" className="btn-ghost !py-2.5 !px-5 !text-[11px]"><ImageIcon className="w-3.5 h-3.5" /> My Photos</Link>
        <Link to="/customer/downloads" data-testid="customer-qa-downloads" className="btn-ghost !py-2.5 !px-5 !text-[11px]"><Download className="w-3.5 h-3.5" /> My Downloads</Link>
        <Link to="/customer/purchases" data-testid="customer-qa-purchases" className="btn-ghost !py-2.5 !px-5 !text-[11px]"><History className="w-3.5 h-3.5" /> Purchase History</Link>
        <Link to="/customer/credits" data-testid="customer-qa-credits" className="btn-ghost !py-2.5 !px-5 !text-[11px]"><Coins className="w-3.5 h-3.5" /> Credit History</Link>
        <Link to="/customer/profile" data-testid="customer-qa-profile" className="btn-ghost !py-2.5 !px-5 !text-[11px]"><User className="w-3.5 h-3.5" /> My Profile</Link>
        <Link to="/contact" data-testid="customer-qa-contact" className="btn-ghost !py-2.5 !px-5 !text-[11px]"><Mail className="w-3.5 h-3.5" /> Contact Support</Link>
        <Link to="/hire" data-testid="customer-qa-hire" className="btn-ghost !py-2.5 !px-5 !text-[11px]"><Phone className="w-3.5 h-3.5" /> Hire Us</Link>
      </div>

      {/* Credit history */}
      <section data-testid="customer-credit-history">
        <div className="flex items-end justify-between mb-5">
          <h2 className="font-display text-2xl sm:text-3xl text-chrome">Recent Credit Activity</h2>
          <Link to="/customer/credits" data-testid="customer-credits-view-all" className="text-[11px] tracking-luxe uppercase text-white/55 hover:text-white inline-flex items-center gap-1">Full History <ArrowRight className="w-3 h-3" /></Link>
        </div>
        <CreditHistory />
      </section>

      {/* Featured events */}
      <section>
        <div className="flex items-end justify-between mb-5">
          <h2 className="font-display text-2xl sm:text-3xl text-chrome">Featured Events</h2>
          <Link to="/events" className="text-[11px] tracking-luxe uppercase text-white/55 hover:text-white inline-flex items-center gap-1">View All <ArrowRight className="w-3 h-3" /></Link>
        </div>
        {loading ? <p className="text-sm text-white/40">Loading…</p> :
         events.length === 0 ? (
          <div className="glass chrome-border rounded-md p-10 text-center text-white/55">
            No event galleries are available yet. Please check back soon.
          </div>
         ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.slice(0, 6).map((e) => (
              <Link key={e.id} to={`/events/${e.slug}`} data-testid={`featured-event-${e.id}`} className="group photo-frame chrome-border aspect-[4/5] block overflow-hidden">
                {e.cover_image ? <img src={e.cover_image} alt={e.title} className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full bg-black/60 flex items-center justify-center text-white/30"><Camera className="w-6 h-6" /></div>}
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.92))" }} />
                <div className="absolute top-3 left-3"><span className="text-[10px] tracking-luxe uppercase px-2 py-1 rounded-full glass-strong border border-white/15 text-white/85">{e.category}</span></div>
                <div className="absolute bottom-0 inset-x-0 p-4">
                  <p className="text-[10px] tracking-luxe uppercase text-white/55">{e.date}</p>
                  <h3 className="font-display text-xl sm:text-2xl text-chrome">{e.title}</h3>
                  <p className="text-[10px] tracking-luxe uppercase text-white/45 mt-1">{e.photo_count} photos · {e.subfolder_count} folders</p>
                </div>
              </Link>
            ))}
          </div>
         )
        }
      </section>

      <div className="glass chrome-border rounded-md p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <MapPin className="w-4 h-4 text-[#18d3ff] shrink-0" />
        <p className="text-sm text-white/65 font-body flex-1">
          Looking for a specific photo or need help? Our team is one message away.
        </p>
        <Link to="/contact" className="btn-ghost !py-2 !px-4 !text-[10px]">Get Help</Link>
      </div>
    </div>
  );
}
